import { whenForegroundQuiet } from "@/shared/api/activity";
import type { ReadLane } from "@/shared/api/client";
import { loadAccess } from "@/shared/api/grants";
import { getIdentity } from "@/shared/api/identity";
import { currentSession } from "@/shared/api/session";
import { createLimiter } from "@/shared/concurrency";
import {
  type InstructionSummary,
  listAdvisorPeople,
  listClientLinks,
  listFirmAdvisors,
  readClientName,
  readInstructionSuggestionId,
  readInstructionSummaries,
  readLinkSubject,
  readPersonName,
  readPortfolioName,
  readSetQuantifiedAt,
  readSuggestedPortfolios,
  readSuggestionById,
  type SuggestedPortfolioWire,
} from "@/features/order-instructions/api/order-reads";
import {
  type ClientLink,
  type InstructionFacts,
  isSuggestedStatus,
  type OrderRow,
  type SuggestedStatus,
  type UnreadClient,
} from "@/features/order-instructions/lib/order-stages";

export interface StageList {
  state: "loading" | "ready" | "error";
  rows: OrderRow[];
  unread: UnreadClient[];
  total: number;
  read: number;
  scope: StageScope;
  widenFailed: boolean;
  instructionsFailed: boolean;
  own: { advisorId: string; name: string | null } | null;
}

export type StageScope = "own" | "all";

export function subscribeStageList(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function stageListSnapshot(): StageList {
  return held !== null && held.userId === sessionUserId() ? held.snapshot : INITIAL;
}

export function watchStageList(): () => void {
  watchers += 1;
  readSlot.wake();
  void openStageList();
  return () => {
    watchers -= 1;
    if (watchers === 0 && held && !prefetching) stopReading(held);
  };
}

export function prefetchStageList(): () => void {
  const cancel = new AbortController();
  whenForegroundQuiet(PREFETCH_AFTER_MS, cancel.signal).then(
    () => {
      if (cancel.signal.aborted) return;
      prefetching = true;
      const userId = sessionUserId();
      if (!userId) return;
      if (held?.userId === userId && held.snapshot.state !== "error") void readPending(held);
      else void loadStageList(userId);
    },
    () => undefined,
  );
  return () => {
    cancel.abort();
    prefetching = false;
  };
}

export function freshenStageList(): void {
  const userId = sessionUserId();
  const list = held;
  if (!userId || list?.userId !== userId || (watchers === 0 && !prefetching)) return;
  if (list.snapshot.state === "error") {
    void loadStageList(userId);
  } else if (list.readAt !== null && Date.now() - list.readAt >= REFRESH_AFTER_MS) {
    void refreshStageList(list);
  }
}

export async function openStageList(): Promise<void> {
  const userId = sessionUserId();
  if (held?.userId === userId && held.snapshot.state !== "error") {
    if (held.readAt !== null && Date.now() - held.readAt >= REFRESH_AFTER_MS) {
      await refreshStageList(held);
      return;
    }
    await readPending(held);
    return;
  }
  await loadStageList(userId);
}

export async function reloadStageList(): Promise<void> {
  clientNames.clear();
  linkSubjects.clear();
  portfolioNames.clear();
  advisorNames.clear();
  const list = held;
  if (list?.userId === sessionUserId() && list.snapshot.state !== "error") {
    await refreshStageList(list);
    return;
  }
  await loadStageList(sessionUserId());
}

export async function widenStageList(): Promise<void> {
  const list = held;
  if (!list || list.scope === "all") return;
  list.scope = "all";
  list.widenFailed = false;
  publish(list, "loading");
  let links: ClientLink[];
  try {
    links = await listClientLinks(null, lane());
  } catch {
    list.scope = "own";
    list.widenFailed = true;
    publish(list, list.pending?.size ? "loading" : "ready");
    return;
  }
  if (held !== list) return;
  const known = new Set(list.links.map((link) => link.key));
  const added = links.filter((link) => !known.has(link.key));
  list.links = [...list.links, ...added];
  list.pending ??= new Set();
  for (const link of added) list.pending.add(link.key);
  publish(list, "loading");
  list.instructionReads = alongside(list.instructionReads, readInstructions(list));
  await readPending(list);
}

export async function retryClient(clientKey: string): Promise<void> {
  const list = held;
  const link = list?.links.find((candidate) => candidate.key === clientKey);
  const unread = list?.unread.get(clientKey);
  if (!list || !link || !unread || unread.retrying) return;
  list.unread.set(clientKey, { ...unread, retrying: true });
  publish(list, list.snapshot.state);
  await readSlot(() => readInto(list, link));
  publish(list, list.snapshot.state);
}

export async function refreshClient(clientKey: string): Promise<void> {
  const list = held;
  if (!list) return;
  const link = list.links.find((candidate) => candidate.key === clientKey) ?? null;
  const [rows] = await Promise.all([
    link ? clientRows(link, list.advisorPeople, lane()).catch(() => null) : null,
    rereadClientInstructions(list, clientKey, link),
  ]);
  if (held !== list) return;
  if (link && rows !== null) list.rowsByClient.set(link.key, rows);
  publish(list, list.snapshot.state);
}

export function staleStageClient(kind: ClientLink["kind"], subjectId: string): void {
  const list = held;
  const link = list?.links.find(
    (candidate) => candidate.kind === kind && candidate.subjectId === subjectId,
  );
  if (!list || !link || list.pending === null || list.pending.has(link.key)) return;
  list.pending.add(link.key);
  list.read -= 1;
  void readPending(list);
}

function sessionUserId(): string {
  return currentSession()?.userId ?? "";
}

function alongside(earlier: Promise<void>, later: Promise<void>): Promise<void> {
  return Promise.all([earlier, later]).then(() => undefined);
}

async function rereadClientInstructions(
  list: HeldList,
  clientKey: string,
  link: ClientLink | null,
): Promise<void> {
  const known = [...list.instructionSummaries.values()].filter(
    (summary) => clientKeyOf(summary) === clientKey,
  );
  const advisors = [
    ...new Set(
      [...known.map((summary) => summary.advisorId), link?.advisorId ?? null].filter(
        (advisor): advisor is string => advisor !== null && list.instructionAdvisors.has(advisor),
      ),
    ),
  ];
  const answers = await Promise.all(
    advisors.map((advisor) => readInstructionSummaries(advisor, lane()).catch(() => null)),
  );
  if (held !== list) return;
  const answered = new Set(advisors.filter((_, index) => answers[index] !== null));
  const current = answers
    .flatMap((summaries) => summaries ?? [])
    .filter((summary) => clientKeyOf(summary) === clientKey);
  const listed = new Set(current.map((summary) => summary.id));
  for (const summary of known) {
    if (summary.advisorId && answered.has(summary.advisorId) && !listed.has(summary.id)) {
      forgetInstruction(list, summary.id);
    }
  }
  for (const summary of current) list.instructionSummaries.set(summary.id, summary);
  await Promise.all(current.map((summary) => readInstructionRow(list, summary, lane())));
}

function forgetInstruction(list: HeldList, instructionId: string): void {
  list.instructionSummaries.delete(instructionId);
  for (const [suggestionId, facts] of list.instructionFacts) {
    if (facts.id !== instructionId) continue;
    list.instructionFacts.delete(suggestionId);
    list.instructions.delete(suggestionId);
  }
}

function lane(signal?: AbortSignal): ReadLane {
  return { signal, background: watchers === 0 };
}

function stopReading(list: HeldList): void {
  list.reader?.abort();
  list.reader = null;
}

async function refreshStageList(list: HeldList): Promise<void> {
  let links: ClientLink[];
  try {
    links = await listClientLinks(
      list.scope === "own" ? (list.own?.advisorId ?? null) : null,
      lane(),
    );
  } catch {
    return;
  }
  if (held !== list) return;
  list.links = ownFirst(links, list.own?.advisorId ?? null);
  const linked = new Set(list.links.map((link) => link.key));
  for (const key of [...list.rowsByClient.keys()])
    if (!linked.has(key)) list.rowsByClient.delete(key);
  for (const key of [...list.unread.keys()]) if (!linked.has(key)) list.unread.delete(key);
  list.pending = linked;
  list.read = 0;
  list.readAt = null;
  list.instructionAdvisors.clear();
  list.widenFailed = false;
  publish(list, "loading");
  list.instructionReads = alongside(
    list.instructionReads,
    readInstructions(list, { refresh: true }),
  );
  await readPending(list);
}

async function readPending(list: HeldList): Promise<void> {
  const pending = list.pending;
  if (pending === null || list.reader !== null || (watchers === 0 && !prefetching)) return;
  const reader = new AbortController();
  list.reader = reader;
  await Promise.all(
    list.links
      .filter((link) => pending.has(link.key))
      .map((link) =>
        readSlot(async () => {
          if (reader.signal.aborted || !pending.has(link.key)) return;
          if (watchers === 0) await whenForegroundQuiet();
          if (reader.signal.aborted || !pending.has(link.key)) return;
          if (!(await readInto(list, link, lane(reader.signal)))) return;
          pending.delete(link.key);
          list.read += 1;
          publish(list, "loading");
        }),
      ),
  );
  if (list.reader === reader) list.reader = null;
  if (reader.signal.aborted || held !== list) return;
  if (list.pending !== pending || pending.size > 0) {
    await readPending(list);
    return;
  }
  await list.instructionReads;
  if (held !== list || list.pending !== pending || pending.size > 0) return;
  list.readAt = Date.now();
  if (list.snapshot.state !== "ready") publish(list, "ready");
}

async function loadStageList(userId: string): Promise<void> {
  if (held) stopReading(held);
  const list: HeldList = {
    userId,
    scope: "own",
    widenFailed: false,
    instructionsFailed: false,
    own: null,
    links: [],
    pending: null,
    reader: null,
    readAt: null,
    ownerCorporationId: null,
    instructionAdvisors: new Set(),
    instructionSummaries: new Map(),
    instructionFacts: new Map(),
    instructions: new Map(),
    instructionReads: Promise.resolve(),
    advisorPeople: Promise.resolve(new Map()),
    rowsByClient: new Map(),
    unread: new Map(),
    read: 0,
    snapshot: INITIAL,
  };
  held = list;
  publish(list, "loading");
  list.advisorPeople = listAdvisorPeople(lane()).catch(() => new Map<string, string>());
  try {
    const [identity, access] = await Promise.all([
      getIdentity().catch(() => null),
      loadAccess().catch(() => null),
    ]);
    const ownAdvisorId = identity?.financialAdvisorId ?? null;
    const profile = identity?.advisorProfile ?? null;
    list.ownerCorporationId = identity?.corporationId ?? null;
    list.own = ownAdvisorId
      ? {
          advisorId: ownAdvisorId,
          name: profile ? `${profile.firstName} ${profile.lastName}`.trim() || null : null,
        }
      : null;
    list.scope = ownAdvisorId !== null && access?.superuser !== true ? "own" : "all";
    const links = await listClientLinks(list.scope === "own" ? ownAdvisorId : null, lane());
    list.links = ownFirst(links, ownAdvisorId);
  } catch {
    if (held === list) publish(list, "error");
    return;
  }
  if (held !== list) return;
  list.pending = new Set(list.links.map((link) => link.key));
  publish(list, "loading");
  list.instructionReads = readInstructions(list);
  await readPending(list);
}

async function readInstructions(
  list: HeldList,
  options: { refresh?: boolean } = {},
): Promise<void> {
  const own = list.own?.advisorId ?? null;
  let advisors: string[];
  try {
    advisors =
      list.scope === "own"
        ? own === null
          ? []
          : [own]
        : await listFirmAdvisors(list.ownerCorporationId, lane());
  } catch {
    list.instructionsFailed = true;
    publish(list, list.snapshot.state);
    return;
  }
  const fresh = advisors.filter((advisor) => !list.instructionAdvisors.has(advisor));
  for (const advisor of fresh) list.instructionAdvisors.add(advisor);
  const answers = await Promise.all(
    fresh.map((advisor) => readInstructionSummaries(advisor, lane()).catch(() => null)),
  );
  if (held !== list) return;
  const unanswered = new Set(fresh.filter((_, index) => answers[index] === null));
  for (const advisor of unanswered) list.instructionAdvisors.delete(advisor);
  if (fresh.length > 0) list.instructionsFailed = unanswered.size > 0;
  const summaries = answers.flatMap((answer) => answer ?? []);
  if (options.refresh) {
    const seen = new Set(summaries.map((summary) => summary.id));
    for (const [id, summary] of [...list.instructionSummaries]) {
      if (seen.has(id) || (summary.advisorId && unanswered.has(summary.advisorId))) continue;
      forgetInstruction(list, id);
    }
  }
  for (const summary of summaries) list.instructionSummaries.set(summary.id, summary);
  await Promise.all(
    summaries.map((summary) =>
      readSlot.first(async () => {
        if (watchers === 0) await whenForegroundQuiet();
        await readInstructionRow(list, summary, lane());
      }),
    ),
  );
}

async function readInstructionRow(
  list: HeldList,
  summary: InstructionSummary,
  readLane: ReadLane,
): Promise<void> {
  const suggestionId = await readInstructionSuggestionId(summary, readLane).catch(() =>
    instructionUnread(list),
  );
  if (held !== list || suggestionId === null) return;
  const known = list.instructionFacts.get(suggestionId);
  if (known && known.id !== summary.id && supersedes(known, summary, list)) return;
  list.instructionFacts.set(suggestionId, {
    id: summary.id,
    status: summary.status,
    documentFileId: summary.documentFileId,
    sealedFileId: summary.sealedFileId,
    signers: summary.signers,
  });
  publish(list, list.snapshot.state);
  if (readFromClient(list, suggestionId)) return;
  const suggestion = await readSuggestionById(suggestionId, readLane).catch(() =>
    instructionUnread(list),
  );
  if (held !== list || !suggestion || !isSuggestedStatus(suggestion.status)) return;
  const link = list.links.find((candidate) => candidate.key === clientKeyOf(summary)) ?? null;
  const subjectId =
    link?.subjectId ??
    (await remembered(linkSubjects, clientKeyOf(summary), () =>
      readLinkSubject(summary.kind, summary.linkId, readLane),
    ));
  const [clientName, advisorName] = await Promise.all([
    remembered(clientNames, clientKeyOf(summary), () =>
      instructionClientName(summary, subjectId, readLane),
    ),
    advisorNameOf(summary.advisorId, list.advisorPeople, readLane),
  ]);
  const row = await suggestionRow(
    suggestion as SuggestedPortfolioWire & { status: SuggestedStatus },
    {
      clientKey: clientKeyOf(summary),
      subjectId,
      clientName,
      advisorId: summary.advisorId,
      advisorName,
    },
    readLane,
  );
  if (held !== list) return;
  list.instructions.set(row.id, row);
  publish(list, list.snapshot.state);
}

function instructionUnread(list: HeldList): null {
  list.instructionsFailed = true;
  return null;
}

function supersedes(known: InstructionFacts, summary: InstructionSummary, list: HeldList): boolean {
  const canceled = (status: string) => status === "CANCELED";
  if (canceled(known.status) !== canceled(summary.status)) return canceled(summary.status);
  const knownAt = list.instructionSummaries.get(known.id)?.createdAt ?? "";
  return knownAt > (summary.createdAt ?? "");
}

function readFromClient(list: HeldList, suggestionId: string): boolean {
  return list.links.some((link) =>
    (list.rowsByClient.get(link.key) ?? []).some((row) => row.id === suggestionId),
  );
}

async function instructionClientName(
  summary: InstructionSummary,
  subjectId: string | null,
  readLane: ReadLane,
): Promise<string | null> {
  if (subjectId === null) return null;
  return readClientName(
    {
      key: clientKeyOf(summary),
      kind: summary.kind,
      linkId: summary.linkId,
      subjectId,
      advisorId: summary.advisorId,
    },
    readLane,
  );
}

function clientKeyOf(summary: Pick<InstructionSummary, "kind" | "linkId">): string {
  return `${summary.kind}:${summary.linkId}`;
}

function publish(list: HeldList, state: StageList["state"]): void {
  if (held !== list) return;
  const fromClients = list.links.flatMap((link) => list.rowsByClient.get(link.key) ?? []);
  const listed = new Set(fromClients.map((row) => row.id));
  const withFacts = (row: OrderRow): OrderRow => {
    const instruction = list.instructionFacts.get(row.id) ?? null;
    return { ...row, instruction, orderInstructionId: instruction?.id ?? null };
  };
  list.snapshot = {
    state,
    rows: [
      ...fromClients,
      ...[...list.instructions.values()].filter((row) => !listed.has(row.id)),
    ].map(withFacts),
    unread: list.links.flatMap((link) => list.unread.get(link.key) ?? []),
    total: list.links.length,
    read: list.read,
    scope: list.scope,
    widenFailed: list.widenFailed,
    instructionsFailed: list.instructionsFailed,
    own: list.own,
  };
  for (const listener of listeners) listener();
}

async function readInto(
  list: HeldList,
  link: ClientLink,
  readLane: ReadLane = lane(),
): Promise<boolean> {
  if (held !== list) return false;
  try {
    const rows = await clientRows(link, list.advisorPeople, readLane);
    if (readLane.signal?.aborted) return false;
    list.rowsByClient.set(link.key, rows);
    list.unread.delete(link.key);
  } catch {
    if (readLane.signal?.aborted) return false;
    list.unread.set(link.key, await unreadClient(link, list.advisorPeople, readLane));
  }
  return true;
}

async function clientRows(
  link: ClientLink,
  advisorPeople: Promise<Map<string, string>>,
  readLane: ReadLane,
): Promise<OrderRow[]> {
  const suggestions = (await readSuggestedPortfolios(link, readLane)).filter(
    (suggestion): suggestion is SuggestedPortfolioWire & { status: SuggestedStatus } =>
      isSuggestedStatus(suggestion.status),
  );
  if (suggestions.length === 0) return [];
  const shared = { background: readLane.background };
  const [clientName, advisorName] = await Promise.all([
    remembered(clientNames, link.key, () => readClientName(link, shared)),
    advisorNameOf(link.advisorId, advisorPeople, shared),
  ]);
  return Promise.all(
    suggestions.map((suggestion) =>
      suggestionRow(
        suggestion,
        {
          clientKey: link.key,
          subjectId: link.subjectId,
          clientName,
          advisorId: link.advisorId,
          advisorName,
        },
        readLane,
      ),
    ),
  );
}

async function suggestionRow(
  suggestion: SuggestedPortfolioWire & { status: SuggestedStatus },
  owner: Pick<OrderRow, "clientKey" | "subjectId" | "clientName" | "advisorId" | "advisorName">,
  readLane: ReadLane,
): Promise<OrderRow> {
  const sourceId = text(suggestion.quant_source_portfolio_id);
  const transactionSetId = text(suggestion.quant_portfolio_transaction_set_id);
  const [portfolioName, pricedAt] = await Promise.all([
    sourceId
      ? remembered(portfolioNames, sourceId, () =>
          readPortfolioName(sourceId, { background: readLane.background }),
        )
      : null,
    transactionSetId && suggestion.status === "APPROVED"
      ? readSetQuantifiedAt(transactionSetId, readLane).catch(() => null)
      : null,
  ]);
  return {
    id: String(suggestion.id),
    ...owner,
    portfolioName,
    status: suggestion.status,
    createdAt: text(suggestion.awaiting_approval_at),
    approvedAt: text(suggestion.approved_at),
    inExecutionAt: text(suggestion.in_execution_at),
    executedAt: text(suggestion.executed_at),
    pricedAt,
    transactionSetId,
    sourcePortfolioId: sourceId,
    orderInstructionId: null,
    instruction: null,
    presentationFileId: text(suggestion.presentation_binary_file_id),
    orderListFileId: text(suggestion.order_instruction_binary_file_id),
  };
}

async function unreadClient(
  link: ClientLink,
  advisorPeople: Promise<Map<string, string>>,
  readLane: ReadLane,
): Promise<UnreadClient> {
  const shared = { background: readLane.background };
  const [clientName, advisorName] = await Promise.all([
    remembered(clientNames, link.key, () => readClientName(link, shared)),
    advisorNameOf(link.advisorId, advisorPeople, shared),
  ]);
  return {
    clientKey: link.key,
    clientName,
    advisorId: link.advisorId,
    advisorName,
    retrying: false,
  };
}

async function remembered(
  cache: Map<string, Promise<string | null>>,
  key: string,
  read: () => Promise<string | null>,
): Promise<string | null> {
  let pending = cache.get(key);
  if (!pending) {
    pending = read().catch(() => {
      cache.delete(key);
      return null;
    });
    cache.set(key, pending);
  }
  return pending;
}

async function advisorNameOf(
  advisorId: string | null,
  advisorPeople: Promise<Map<string, string>>,
  readLane: ReadLane,
): Promise<string | null> {
  if (!advisorId) return null;
  const individualId = (await advisorPeople).get(advisorId);
  if (!individualId) return null;
  return remembered(advisorNames, individualId, () =>
    readPersonName(individualId, { background: readLane.background }),
  );
}

function ownFirst(links: readonly ClientLink[], ownAdvisorId: string | null): ClientLink[] {
  const own = (link: ClientLink) => link.advisorId !== null && link.advisorId === ownAdvisorId;
  return [...links.filter(own), ...links.filter((link) => !own(link))];
}

function text(value: string | number | null | undefined): string | null {
  return value === null || value === undefined || value === "" ? null : String(value);
}

interface HeldList {
  userId: string;
  scope: StageScope;
  widenFailed: boolean;
  instructionsFailed: boolean;
  own: StageList["own"];
  links: ClientLink[];
  pending: Set<string> | null;
  reader: AbortController | null;
  readAt: number | null;
  ownerCorporationId: string | null;
  instructionAdvisors: Set<string>;
  instructionSummaries: Map<string, InstructionSummary>;
  instructionFacts: Map<string, InstructionFacts>;
  instructions: Map<string, OrderRow>;
  instructionReads: Promise<void>;
  advisorPeople: Promise<Map<string, string>>;
  rowsByClient: Map<string, OrderRow[]>;
  unread: Map<string, UnreadClient>;
  read: number;
  snapshot: StageList;
}

export const ORDER_READ_CONCURRENCY = 3;
export const PREFETCH_CONCURRENCY = 2;
export const PREFETCH_AFTER_MS = 1_500;
export const REFRESH_AFTER_MS = 60_000;

const INITIAL: StageList = {
  state: "loading",
  rows: [],
  unread: [],
  total: 0,
  read: 0,
  scope: "own",
  widenFailed: false,
  instructionsFailed: false,
  own: null,
};
let watchers = 0;
let prefetching = false;
const readSlot = createLimiter(() =>
  watchers > 0 ? ORDER_READ_CONCURRENCY : PREFETCH_CONCURRENCY,
);
const listeners = new Set<() => void>();
const clientNames = new Map<string, Promise<string | null>>();
const linkSubjects = new Map<string, Promise<string | null>>();
const portfolioNames = new Map<string, Promise<string | null>>();
const advisorNames = new Map<string, Promise<string | null>>();
let held: HeldList | null = null;
